import {BaseMutation} from '@app/editor/mutations/base-mutation';
import {message} from '@common/i18n/message';
import {
  ModifyAttributes,
} from '@app/editor/mutations/attributes/modify-attributes';
import {ResizeNode} from '@app/editor/mutations/style/resize-node';
import {CssStyle, CssKey} from '@app/editor/mutations/style/css-style';
import {isAbsoluteUrl} from '@common/utils/urls/is-absolute-url';
import {getBootstrapData} from '@common/core/bootstrap-data/use-backend-bootstrap-data';
import {
  HoveredElementOverlay,
} from '@app/editor/elements/element-overlays/element-overlays';
import {mutationState} from '@app/editor/state/mutation-store';

export class UpdateImage extends BaseMutation{
  private modifyAttributes: ModifyAttributes;
  private resizeNode: ResizeNode;
  private targetNode: HTMLElement;
  changes = {new: null, old: null};  // these are both null as they are unused

  displayName = message("Updated image");
  constructor(
    modifyAttributes: ModifyAttributes,
    resizeNode: ResizeNode,
    targetNode: HTMLElement,
  ) {
    super(modifyAttributes.getNodeId()!);
    this.resizeNode = resizeNode;
    this.modifyAttributes = modifyAttributes;
    this.targetNode = targetNode;
  }

  init(): this {
    return this;
  }

  execute(): boolean {
    // Directly lock dimensions on the DOM node before anything else
    this.applyStyles(this.resizeNode.changes.new);
    const resized = this.resizeNode.init().execute();
    const modified = this.modifyAttributes.init().execute();
    // Also apply directly in case the mutation pipeline missed the iframe node
    this.applyStyles(this.resizeNode.changes.new);
    this.applyAttributes(this.modifyAttributes.changes.new);
    return modified || resized;
  }

  undo(): boolean {
    const modified = this.modifyAttributes.undo();
    const resized = this.resizeNode.undo();
    // Directly restore old styles and attributes on the actual DOM node
    this.applyStyles(this.resizeNode.changes.old);
    this.applyAttributes(this.modifyAttributes.changes.old);
    return modified || resized;
  }

  redo(): boolean {
    // Directly apply new styles first to prevent container stretching
    this.applyStyles(this.resizeNode.changes.new);
    const resized = this.resizeNode.redo();
    const modified = this.modifyAttributes.redo();
    // Ensure both styles and src are applied on the actual DOM node
    this.applyStyles(this.resizeNode.changes.new);
    this.applyAttributes(this.modifyAttributes.changes.new);
    return modified || resized;
  }

  /**
   * Directly set inline styles on the stored DOM node reference.
   * This bypasses the mutation pipeline entirely, ensuring styles are applied
   * regardless of stale document references or styleEquals issues.
   */
  private applyStyles(styles: CssStyle): void {
    for (const key of Object.keys(styles)) {
      const value = styles[key as CssKey];
      if (value !== undefined) {
        this.targetNode.style[key as any] = value;
      }
    }
  }

  /**
   * Directly set attributes on the stored DOM node reference.
   */
  private applyAttributes(attrs: Record<string, string | null | undefined>): void {
    for (const [key, value] of Object.entries(attrs)) {
      if (value == null) {
        this.targetNode.removeAttribute(key);
      } else {
        this.targetNode.setAttribute(key, value);
      }
    }
  }

  protected executeMutation(doc: Document): boolean {
    return true;
  }

  protected undoMutation(doc: Document): boolean {
    return true;
  }
}

/**
 * Replace an image node's src and lock it to its original rendered dimensions.
 * Shared by both the AI image action and the manual upload action.
 *
 * If the caller already captured the old dimensions (e.g. before an async
 * upload), pass them as preOldWidth/preOldHeight to avoid reading stale values.
 */
export function replaceImageOnNode(
  node: HTMLElement,
  imageUrl: string,
  preOldWidth?: number,
  preOldHeight?: number,
): void {
  const absoluteUrl = isAbsoluteUrl(imageUrl)
    ? imageUrl
    : `${getBootstrapData().settings.base_url}/${imageUrl}`;

  // Use pre-captured dimensions if provided, otherwise read now.
  // offsetWidth/offsetHeight are direct DOM properties that work reliably
  // even for elements inside the editor iframe.
  const oldWidth = preOldWidth ?? node.offsetWidth;
  const oldHeight = preOldHeight ?? node.offsetHeight;

  const modifyAttributes = new ModifyAttributes(node, {src: absoluteUrl});

  // Wait for the new image to load so we have its natural dimensions
  const imageEl = document.createElement('img');
  imageEl.onload = () => {
    const resizeNode = buildResizeNode(node, oldWidth, oldHeight, imageEl);

    mutationState().executeMutation(
      new UpdateImage(modifyAttributes, resizeNode, node),
      {lastInSession: true},
    );
  };
  imageEl.onerror = () => {
    // Still apply the src change even if the image fails to load,
    // so the user sees the broken state and can undo it.
    const resizeNode = buildResizeNode(node, oldWidth, oldHeight, imageEl);
    mutationState().executeMutation(
      new UpdateImage(modifyAttributes, resizeNode, node),
      {lastInSession: true},
    );
  };
  imageEl.src = absoluteUrl;

  HoveredElementOverlay.hide();
}

function buildResizeNode(
  node: HTMLElement,
  oldWidth: number,
  oldHeight: number,
  imageEl: HTMLImageElement,
): ResizeNode {
  let width: number;
  let height: number;

  if (oldWidth > 0 && oldHeight > 0) {
    width = oldWidth;
    height = oldHeight;
  } else {
    // Fallback: cap natural size to a reasonable maximum
    const maxDim = 300;
    const scale = Math.min(1, maxDim / Math.max(imageEl.naturalWidth, imageEl.naturalHeight));
    width = imageEl.naturalWidth * scale;
    height = imageEl.naturalHeight * scale;
  }

  return new ResizeNode(
    {width: `${width}px`, height: `${height}px`, maxWidth: '100%'},
    node,
  );
}
